import type { CSSProperties, FC, MouseEvent, ReactElement, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { Button, Dropdown, Input, Select, Switch, Tag } from 'antd';
import { EllipsisOutlined, SyncOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';

import { useTpoGenConfig, useCustomerConfig } from '../../context/CustomerConfigContext';
import {
    TPO_SCENARIO_COMPARISON_ACTIONS_BTN,
    TPO_SCENARIO_COMPARISON_ADD_SCENARIO_BTN,
    TPO_SCENARIO_COMPARISON_CHANGE_BASE_BTN,
    TPO_SCENARIO_COMPARISON_KPI_SELECT,
    TPO_SCENARIO_COMPARISON_PERCENT_SWITCH,
    TPO_SCENARIO_COMPARISON_SAVE_BTN,
    TPO_SCENARIO_COMPARISON_SEARCH_INPUT,
    TPO_SCENARIO_COMPARISON_TOOLBAR,
    tpoOptionTestId,
    tpoPartTestId,
} from '../../config/testIds';

const KPI_SELECT_ALL_TEST_ID = tpoPartTestId(TPO_SCENARIO_COMPARISON_KPI_SELECT, 'select_all');

const SELECT_ALL_STYLE: CSSProperties = {
    padding: '5px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid #f0f0f0',
    fontSize: 12,
    color: '#1677ff',
};

const renderTag = ({ label }: { label: ReactNode }) => <Tag>{label}</Tag>;

const preventBlur = (e: MouseEvent) => e.preventDefault();

interface ScenarioComparisonToolbarProps {
    selectedKpiKeys: string[];
    onKpiChange: (keys: string[]) => void;
    search: string;
    onSearchChange: (value: string) => void;
    onChangeBaseScenario: () => void;
    onAddScenario: () => void;
    onSave: () => void;
    onDelete: () => void;
    onRefresh: () => void;
    onExport: () => void;
    isSaving?: boolean;
    hasBaseScenario?: boolean;
    /** False while the base plan is missing, when there is no grid to export. */
    canExport?: boolean;
    showPercent: boolean;
    onShowPercentChange: (value: boolean) => void;
}

const ScenarioComparisonToolbar: FC<ScenarioComparisonToolbarProps> = ({
    selectedKpiKeys,
    onKpiChange,
    search,
    onSearchChange,
    onChangeBaseScenario,
    onAddScenario,
    onSave,
    onDelete,
    onRefresh,
    onExport,
    isSaving,
    hasBaseScenario,
    canExport,
    showPercent,
    onShowPercentChange,
}) => {
    const { scenarioComparisonConfig, labels } = useTpoGenConfig();
    const { scenarioComparisonMenuItems } = scenarioComparisonConfig;
    const { kpiCatalog } = useCustomerConfig();

    const kpiOptions = useMemo(
        () =>
            kpiCatalog.map((kpi) => ({
                label: kpi.name,
                value: kpi.key,
                'data-testid': tpoOptionTestId(TPO_SCENARIO_COMPARISON_KPI_SELECT, kpi.key),
            })),
        [kpiCatalog],
    );

    const isAllKpisSelected = selectedKpiKeys.length === kpiCatalog.length;

    const handleToggleAllKpis = useCallback(() => {
        onKpiChange(isAllKpisSelected ? [] : kpiCatalog.map((kpi) => kpi.key));
    }, [isAllKpisSelected, kpiCatalog, onKpiChange]);

    const renderKpiDropdown = useCallback(
        (menu: ReactElement) => (
            <>
                <div
                    style={SELECT_ALL_STYLE}
                    onMouseDown={preventBlur}
                    onClick={handleToggleAllKpis}
                    data-testid={KPI_SELECT_ALL_TEST_ID}
                >
                    {isAllKpisSelected
                        ? labels.comparisonReport.deselectAllKpis
                        : labels.comparisonReport.selectAllKpis}
                </div>
                {menu}
            </>
        ),
        [handleToggleAllKpis, isAllKpisSelected, labels.comparisonReport],
    );

    const menuItems: MenuProps['items'] = useMemo(
        () =>
            scenarioComparisonMenuItems.map((item) => {
                if ('type' in item) return item;
                return {
                    key: item.key,
                    label: item.danger ? <span className="text-[#FF4D4F]">{item.label}</span> : item.label,
                    // Without a base plan there is no `base_plan_id` to address the
                    // export to, and the grid it would export is not rendered.
                    disabled: item.disabled || (item.key === 'export' && !canExport),
                    'data-testid': tpoOptionTestId(TPO_SCENARIO_COMPARISON_ACTIONS_BTN, item.key),
                };
            }),
        [scenarioComparisonMenuItems, canExport],
    );

    const handleMenuClick: MenuProps['onClick'] = useCallback(({ key }: { key: string }) => {
        switch (key) {
            case 'refresh':
                onRefresh();
                break;
            case 'delete':
                onDelete();
                break;
            case 'export':
                onExport();
                break;
            default:
                break;
        }
    }, [onRefresh, onDelete, onExport]);

    return (
        <div className="flex items-center justify-between gap-4 flex-wrap" data-testid={TPO_SCENARIO_COMPARISON_TOOLBAR}>
            <Select
                mode="multiple"
                placeholder={labels.comparisonReport.selectKpi}
                options={kpiOptions}
                value={selectedKpiKeys}
                onChange={onKpiChange}
                style={{ minWidth: 240, maxWidth: 400 }}
                allowClear
                showSearch
                optionFilterProp="label"
                tagRender={renderTag}
                maxTagCount="responsive"
                dropdownRender={renderKpiDropdown}
                data-testid={TPO_SCENARIO_COMPARISON_KPI_SELECT}
            />
            <div className="flex items-center gap-3">
                <Input
                    placeholder={labels.comparisonReport.search}
                    prefix={<SearchOutlined />}
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    style={{ width: 160 }}
                    allowClear
                    data-testid={TPO_SCENARIO_COMPARISON_SEARCH_INPUT}
                />
                <div className="flex items-center gap-2">
                    <span className="text-xs text-[#807E86]">{labels.comparisonReport.absToggle}</span>
                    <Switch
                        size="small"
                        checked={showPercent}
                        onChange={onShowPercentChange}
                        data-testid={TPO_SCENARIO_COMPARISON_PERCENT_SWITCH}
                    />
                    <span className="text-xs text-[#807E86]">{labels.comparisonReport.percentToggle}</span>
                </div>
                <Button
                    onClick={onChangeBaseScenario}
                    icon={<SyncOutlined />}
                    className="!flex items-center"
                    data-testid={TPO_SCENARIO_COMPARISON_CHANGE_BASE_BTN}
                >
                    {labels.comparisonReport.changeBaseScenario}
                </Button>
                <Button
                    onClick={onAddScenario}
                    disabled={!hasBaseScenario}
                    icon={<PlusOutlined />}
                    className="!flex items-center justify-center"
                    data-testid={TPO_SCENARIO_COMPARISON_ADD_SCENARIO_BTN}
                >
                    {labels.comparisonReport.addScenarioTitle}
                </Button>

                <Button
                    type="primary"
                    onClick={onSave}
                    loading={isSaving}
                    disabled={!hasBaseScenario}
                    data-testid={TPO_SCENARIO_COMPARISON_SAVE_BTN}
                >
                    {labels.common.save}
                </Button>
                <Dropdown
                    menu={{ items: menuItems, onClick: handleMenuClick }}
                    trigger={['click']}
                    overlayStyle={{ width: '10rem', borderRadius: '0.5rem' }}
                >
                    <Button icon={<EllipsisOutlined />} data-testid={TPO_SCENARIO_COMPARISON_ACTIONS_BTN} />
                </Dropdown>
            </div>
        </div>
    );
};

ScenarioComparisonToolbar.displayName = 'ScenarioComparisonToolbar';
export default ScenarioComparisonToolbar;
